import { FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { formatDate } from '@/lib/datetime';
import { useTimezone } from '@/hooks/useTimezone';
import type { PersonReportFactOut, PersonReportOut } from '@/services/types';

/**
 * One chip per PANEL, not per fact: two facts often sit on one panel (the
 * evidence tier and the validation reading both live on `conselho`), and a
 * chip per fact rendered the same label twice side by side.
 */
function chipsFor(facts: PersonReportFactOut[]): { panel: string; codes: string[] }[] {
  const byPanel = new Map<string, string[]>();
  for (const fact of facts) {
    byPanel.set(fact.panel, [...(byPanel.get(fact.panel) ?? []), fact.code]);
  }
  return [...byPanel].map(([panel, codes]) => ({ panel, codes }));
}

function scrollToPanel(panel: string) {
  document
    .querySelector(`[data-panel="${CSS.escape(panel)}"]`)
    ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

/**
 * A written report on one decision-maker, at the top of the person page.
 *
 * The prose is model-written, and it is shown only because the backend
 * verified it against the figures of the panels below (every number, every
 * cited fact, the careful-language list) and because those figures have not
 * moved since it was written. Each paragraph carries a chip per fact it cites,
 * and the chip scrolls to the panel holding the same figure, so no sentence
 * here is an orphan number.
 *
 * `stale` and `not_generated` render a stated absence rather than nothing: a
 * missing report and a report withheld because the data moved are different
 * facts, and neither is "this person has no behaviour to report".
 *
 * The payload carries one report per locale; the card shows the reader's own
 * language, since every other word on the page follows it. Absent `reports`
 * (a payload from before this field) renders nothing rather than a guess.
 */
export function PersonReportCard({ reports }: { reports?: Record<string, PersonReportOut> }) {
  const { t, i18n } = useTranslation('common');
  const { timezone } = useTimezone();
  const report = reports?.[i18n.language.slice(0, 2)];
  if (!report) return null;

  return (
    <section
      className="space-y-3 rounded-lg border bg-card p-4"
      data-panel="person-report"
      data-report-status={report.status}
      aria-labelledby="person-report-title"
    >
      <h2 id="person-report-title" className="flex items-center gap-2 text-base font-semibold">
        <FileText className="h-4 w-4 text-muted-foreground" aria-hidden />
        {t('personReport.title')}
      </h2>

      {report.status === 'current' ? (
        <>
          <div className="space-y-3 text-sm leading-relaxed">
            {report.paragraphs.map((paragraph, i) => (
              <div key={i} className="space-y-1.5">
                <p>{paragraph.text}</p>
                <div className="flex flex-wrap gap-1.5">
                  {chipsFor(paragraph.facts).map(({ panel, codes }) => (
                    <button
                      key={panel}
                      type="button"
                      onClick={() => scrollToPanel(panel)}
                      className="rounded-full border px-2 py-0.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
                      data-fact={codes.join(' ')}
                    >
                      {t(`personReport.panels.${panel}`)}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <p className="border-t pt-2 text-xs text-muted-foreground">
            {t('personReport.provenance', {
              model: report.model,
              version: report.prompt_version,
              date: report.generated_at ? formatDate(report.generated_at, timezone) : '',
            })}
          </p>
        </>
      ) : (
        <p role="status" className="text-sm text-muted-foreground">
          {t(`personReport.status.${report.status}`)}
        </p>
      )}
    </section>
  );
}
